import React from 'react';
import {
  Brush,
  ArrowRightAlt,
  CropSquare,
  RadioButtonUnchecked,
  Close,
  TextFields,
  Numbers,
  Palette,
  Save,
  FileCopy,
  Clear,
  Undo,
  Redo,
  RestartAlt,
  Image as ImageIcon,
} from '@mui/icons-material';
import {
  hexToRgba,
  extractHexFromRgba,
  extractOpacityFromRgba,
  setRgbaOpacity,
} from '../utils/color';

const TOOLS = [
  { id: 'line', label: 'Freehand', Icon: Brush },
  { id: 'arrow', label: 'Arrow', Icon: ArrowRightAlt },
  { id: 'rect', label: 'Box', Icon: CropSquare },
  { id: 'circle', label: 'Circle', Icon: RadioButtonUnchecked },
  { id: 'x', label: 'X Mark', Icon: Close },
  { id: 'text', label: 'Text', Icon: TextFields },
  { id: 'number', label: 'Number', Icon: Numbers },
];

// A labelled native color swatch + hex readout.
const ColorRow = ({ label, value, onChange }) => (
  <div className="option-row">
    <label className="option-label">
      <Palette className="option-label__icon" />
      {label}
    </label>
    <div className="color-control">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="color-swatch"
      />
      <span className="color-value">{value}</span>
    </div>
  </div>
);

// A labelled range slider with a value readout.
const SliderRow = ({ label, value, min, max, unit, onChange }) => (
  <div className="option-row option-row--column">
    <label className="option-label option-label--row">
      {label}
      <span className="option-value">
        {value}
        {unit}
      </span>
    </label>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="range-slider"
    />
  </div>
);

const SidebarContent = ({
  tool,
  onToolChange,
  prefs,
  setPref,
  onUpload,
  onSave,
  onCopy,
  onClear,
  onUndo,
  onRedo,
  onResetNumbers,
  canUndo,
  canRedo,
  hasImage,
  hasMarks,
}) => {
  const opacity = extractOpacityFromRgba(prefs.circleColor);
  const sharedWidth = (
    <SliderRow
      label="Line Width"
      value={prefs.lineWidth}
      min={1}
      max={30}
      unit="px"
      onChange={(v) => setPref('lineWidth', v)}
    />
  );

  return (
    <div className="sidebar-content">
      {/* Actions */}
      <div className="sidebar-section">
        <div className="sidebar-section-header">Actions</div>
        <div className="sidebar-actions">
          <label htmlFor="sidebar-upload-file" className="action-btn action-btn--upload">
            <ImageIcon className="action-btn__icon" />
            <span>Upload Image</span>
          </label>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="sidebar-upload-file"
            type="file"
            onChange={onUpload}
          />
          <div className="sidebar-actions-row">
            <button className="action-btn action-btn--half" onClick={onUndo} disabled={!canUndo}>
              <Undo className="action-btn__icon" />
              <span>Undo</span>
            </button>
            <button className="action-btn action-btn--half" onClick={onRedo} disabled={!canRedo}>
              <Redo className="action-btn__icon" />
              <span>Redo</span>
            </button>
          </div>
          <button className="action-btn action-btn--save" onClick={onSave} disabled={!hasImage}>
            <Save className="action-btn__icon" />
            <span>Save Image</span>
          </button>
          <button className="action-btn action-btn--copy" onClick={onCopy} disabled={!hasImage}>
            <FileCopy className="action-btn__icon" />
            <span>Copy to Clipboard</span>
          </button>
          <button className="action-btn action-btn--clear" onClick={onClear} disabled={!hasMarks}>
            <Clear className="action-btn__icon" />
            <span>Clear All</span>
          </button>
        </div>
      </div>

      <div className="sidebar-divider" />

      {/* Tool Selection */}
      <div className="sidebar-section">
        <div className="sidebar-section-header">Drawing Tool</div>
        <div className="tool-picker">
          {TOOLS.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`tool-btn ${tool === id ? 'tool-btn--active' : ''}`}
              onClick={() => onToolChange(id)}
            >
              <Icon className="tool-btn__icon" />
              <span className="tool-btn__label">{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="sidebar-divider" />

      {/* Tool Options */}
      <div className="sidebar-section">
        <div className="sidebar-section-header">Tool Options</div>

        {tool === 'line' && (
          <div className="tool-options">
            <ColorRow label="Color" value={prefs.lineColor} onChange={(v) => setPref('lineColor', v)} />
            {sharedWidth}
          </div>
        )}

        {tool === 'arrow' && (
          <div className="tool-options">
            <ColorRow label="Color" value={prefs.arrowColor} onChange={(v) => setPref('arrowColor', v)} />
            {sharedWidth}
          </div>
        )}

        {tool === 'rect' && (
          <div className="tool-options">
            <ColorRow label="Color" value={prefs.rectColor} onChange={(v) => setPref('rectColor', v)} />
            {sharedWidth}
          </div>
        )}

        {tool === 'x' && (
          <div className="tool-options">
            <ColorRow label="Color" value={prefs.xColor} onChange={(v) => setPref('xColor', v)} />
            <SliderRow
              label="Size"
              value={prefs.xMarkSize}
              min={10}
              max={100}
              unit="px"
              onChange={(v) => setPref('xMarkSize', v)}
            />
          </div>
        )}

        {tool === 'circle' && (
          <div className="tool-options">
            <ColorRow
              label="Color"
              value={extractHexFromRgba(prefs.circleColor)}
              onChange={(hex) => setPref('circleColor', hexToRgba(hex, opacity))}
            />
            <SliderRow
              label="Opacity"
              value={Math.round(opacity * 100)}
              min={0}
              max={100}
              unit="%"
              onChange={(v) => setPref('circleColor', setRgbaOpacity(prefs.circleColor, v / 100))}
            />
            <SliderRow
              label="Size"
              value={prefs.circleSize}
              min={10}
              max={100}
              unit="px"
              onChange={(v) => setPref('circleSize', v)}
            />
          </div>
        )}

        {tool === 'text' && (
          <div className="tool-options">
            <ColorRow label="Color" value={prefs.textColor} onChange={(v) => setPref('textColor', v)} />
            <SliderRow
              label="Font Size"
              value={prefs.textSize}
              min={10}
              max={72}
              unit="px"
              onChange={(v) => setPref('textSize', v)}
            />
          </div>
        )}

        {tool === 'number' && (
          <div className="tool-options">
            <ColorRow label="Color" value={prefs.numberColor} onChange={(v) => setPref('numberColor', v)} />
            <SliderRow
              label="Size"
              value={prefs.numberSize}
              min={12}
              max={60}
              unit="px"
              onChange={(v) => setPref('numberSize', v)}
            />
            <button className="action-btn" onClick={onResetNumbers} disabled={!hasMarks}>
              <RestartAlt className="action-btn__icon" />
              <span>Reset Numbering</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarContent;
